import React, { ComponentPropsWithoutRef, ElementType, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { useLocation, useRouteMatch } from 'react-router-dom';

import {
  selectDraggedState,
  selectLocation,
} from '../../../../common/store/item-edit';
import {
  FEEDBACK_WIZARD_STEP,
  compilePath,
  getPathnameMatch,
  namedRoute,
} from '../../../../common/utils/helpers/routes';
import {
  isAddressItem,
  isPOIItem,
} from '../../../../common/utils/helpers/item';
import { selectStyleSelectorShown } from '../../../../common/store/ui';
import WizardLayout from '../../../../common/components/wizard-layout/WizardLayout';
import PoiForm from '../../../../common/components/forms/poi-form/PoiForm';
import EditAddressForm from '../edit-address-form/EditAddressForm';
import { useMobile } from '../../../../common/components/responsive/Responsive';

export interface ItemEditProps
  extends ComponentPropsWithoutRef<ElementType<any>> {
  className?: string;
  item;
  datasource?: string;
}

const ItemEdit = ({ className, item, datasource, ...props }: ItemEditProps) => {
  const itemDragPosition = useSelector(selectLocation);
  const itemMarkerDragged = useSelector(selectDraggedState);

  const isMobile = useMobile();
  const match = useRouteMatch<{ wizardStep: FEEDBACK_WIZARD_STEP }>();
  const isAddressEdit = useMemo(() => isAddressItem(item), [item]);
  const isPoiEdit = useMemo(() => isPOIItem(item), [item]);

  const location = useLocation();

  const styleSelectorShown = useSelector(selectStyleSelectorShown);
  if (isMobile && styleSelectorShown) {
    return null;
  }

  const routablePointsMatch = getPathnameMatch(
    namedRoute(
      'routable-points',
      compilePath('edit', null, {
        wizardStep: FEEDBACK_WIZARD_STEP.routablePoints as FEEDBACK_WIZARD_STEP,
      }),
    ),
    location.pathname,
  );

  let title = 'Edit place details';
  let description;

  const { wizardStep } = match.params;
  if (wizardStep) {
    if (isMobile) {
      switch (wizardStep) {
        case FEEDBACK_WIZARD_STEP.location:
          title = 'Set the exact location on map';
          description = 'Drag the map to edit the address';
          break;

        case FEEDBACK_WIZARD_STEP.feedback:
          if (isAddressEdit) {
            title = 'Edit address';
            description = 'Drag the map to edit the location';
          }
          break;

        case FEEDBACK_WIZARD_STEP.additionalInformation:
          title = 'Add additional information';
          break;
      }
    }

    if (wizardStep === FEEDBACK_WIZARD_STEP.routablePoints) {
      title = 'Set Pickup/Arrival';
      description = 'Set the location pin on the road';

      if (
        isMobile &&
        routablePointsMatch &&
        (routablePointsMatch.params as any).wizardStep
      ) {
        switch ((routablePointsMatch.params as any).wizardStep) {
          case FEEDBACK_WIZARD_STEP.location:
            title = 'Set the exact location on map';
            description = 'Drag the map to edit the point location';
            break;

          case FEEDBACK_WIZARD_STEP.feedback:
            description = null;
            break;
        }
      }
    }
  }

  const shouldShowOuterBackButton =
    isMobile &&
    ((isPoiEdit && wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (isAddressEdit && wizardStep === FEEDBACK_WIZARD_STEP.feedback) ||
      (wizardStep === FEEDBACK_WIZARD_STEP.routablePoints &&
        routablePointsMatch &&
        (routablePointsMatch?.params as any).wizardStep ===
          FEEDBACK_WIZARD_STEP.location));

  const isFullScreen =
    isMobile &&
    ((isPoiEdit &&
      (wizardStep === FEEDBACK_WIZARD_STEP.feedback ||
        wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation)) ||
      (isAddressEdit &&
        wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation) ||
      (wizardStep === FEEDBACK_WIZARD_STEP.routablePoints &&
        routablePointsMatch &&
        (routablePointsMatch?.params as any).wizardStep !==
          FEEDBACK_WIZARD_STEP.location));

  return (
    <WizardLayout
      className={classnames('item-edit', className)}
      title={title}
      hasTitleBackButton={isFullScreen}
      hasOuterBackButton={shouldShowOuterBackButton}
      hasCloseButton={!isFullScreen}
      fullScreen={isFullScreen}
      description={description}
    >
      {isPoiEdit ? (
        // @ts-expect-error not implemented yet
        <PoiForm
          item={item}
          datasource={datasource}
          action="update"
          reason="incorrect_data"
          itemDragPosition={itemDragPosition}
          itemMarkerDragged={itemMarkerDragged}
          {...props}
        />
      ) : (
        // @ts-expect-error not implemented yet
        <EditAddressForm
          item={item}
          itemDragPosition={itemDragPosition}
          itemMarkerDragged={itemMarkerDragged}
          {...props}
        />
      )}
    </WizardLayout>
  );
};

export default ItemEdit;
